import type { UserAccessCookie } from "~/types/auth";
import {
  canCorrectOfficialMaterials,
  isContentQualityReviewerRoute,
} from "~/utils/adminCapabilities";

/** Keep login returns on this origin, including classroom query parameters. */
export const safeAuthReturnPath = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const path = value.trim().replace(/^"|"$/g, "");
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  try {
    const decoded = decodeURIComponent(path);
    if (
      decoded.startsWith("//") ||
      decoded.includes("\\") ||
      [...decoded].some(
        (character) =>
          character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
      ) ||
      /^\/(?:login|school-login|school-access|logout|password-reset)(?:[/?#]|$)/.test(
        decoded,
      )
    )
      return null;
    const parsed = new URL(path, "https://auth.teachplus.invalid");
    if (
      parsed.origin !== "https://auth.teachplus.invalid" ||
      decodeURIComponent(parsed.pathname).startsWith("//") ||
      /^\/(?:login|school-login|school-access|logout|password-reset)(?:[/?#]|$)/.test(
        decodeURIComponent(parsed.pathname),
      )
    )
      return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

/** Saved returns can belong to a previous account; validate the current profile first. */
export const safeAuthReturnPathForUser = (
  value: unknown,
  permissions: UserAccessCookie,
  documentAccess = false,
): string | null => {
  const path = safeAuthReturnPath(value);
  if (!path) return null;
  const pathname = path.split(/[?#]/)[0];
  if (/^\/student-dashboard(?:\/|$)/.test(pathname))
    return permissions.isStudent ? path : null;
  if (/^\/admin-dashboard(?:\/|$)/.test(pathname))
    return permissions.isStaff ||
      permissions.isSuperuser ||
      (canCorrectOfficialMaterials(permissions.adminCapabilities) &&
        isContentQualityReviewerRoute(path))
      ? path
      : null;
  if (/^\/(?:dashboard|school-teacher)(?:\/|$)/.test(pathname))
    return permissions.isTeacher ||
      (documentAccess && /^\/dashboard\/documents(?:\/|$)/.test(pathname))
      ? path
      : null;
  return path;
};

export const getSchoolInvitationToken = (
  value: unknown,
): string | undefined => {
  const path = safeAuthReturnPath(value);
  return path?.match(
    /^\/school-invitations\/([a-zA-Z0-9_-]+)\/?(?:[?#]|$)/,
  )?.[1];
};

/** A recovery email can restore an invitation, never an arbitrary return URL. */
export const schoolInvitationPathFromQuery = (
  value: unknown,
): string | null => {
  if (
    typeof value !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
      value,
    )
  )
    return null;
  return `/school-invitations/${value}`;
};
